import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useDivision } from '../contexts/DivisionContext'
import {
  PlusIcon,
  EditIcon,
  TrashIcon,
  XIcon,
  BuildingIcon,
  BriefcaseIcon,
  HammerIcon,
  WrenchIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  EyeIcon,
} from 'lucide-react'
import { normalizeChecklistItems } from '../lib/checklistTemplates'
import {
  MobilePage,
  MobilePageHeader,
  MobileEmptyState,
  MobileSection,
  mobile,
} from '../components/mobile'

interface ServiceCategory {
  id: string
  name: string
  description?: string
  operational_division_id?: string
  is_active: boolean
  created_at: string
  updated_at: string
  operational_division?: {
    id: string
    name: string
    color_code: string
  }
}

interface ServiceItem {
  id: string
  service_category_id: string
  name: string
  description?: string
  work_description?: string | null
  default_checklist?: Array<{ label: string; sort_order: number; photo_required?: boolean }>
  is_package?: boolean
  unit_type: string
  base_price: number
  is_active: boolean
  created_at: string
  updated_at: string
  // Phase 11: seasonal flag drives visit-group date inputs
  is_seasonal?: boolean
  season_start_month?: number | null
  season_end_month?: number | null
  categories?: {
    id: string
    name: string
    operational_division_id?: string
  }
}

export function ServiceCatalog() {
  const { currentDivision } = useDivision()
  const [categories, setCategories] = useState<ServiceCategory[]>([])
  const [serviceItems, setServiceItems] = useState<ServiceItem[]>([])
  const [divisions, setDivisions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  /** package_item_id → ordered child_item_ids */
  const [packageChildIds, setPackageChildIds] = useState<Record<string, string[]>>({})
  const [childItemIdSet, setChildItemIdSet] = useState<Set<string>>(new Set())
  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(new Set())
  /** When set, next create links the new item as a child of this package */
  const [linkingPackageId, setLinkingPackageId] = useState<string | null>(null)
  const [notification, setNotification] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)
  const [viewingItem, setViewingItem] = useState<ServiceItem | null>(null)

  const showToast = (type: 'success' | 'error', message: string, ms = 3500) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), ms)
  }

  // Category form state
  const [showCategoryForm, setShowCategoryForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null)
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    operational_division_id: '',
    is_active: true,
  })

  // Service item form state
  const [showItemForm, setShowItemForm] = useState(false)
  const [editingItem, setEditingItem] = useState<ServiceItem | null>(null)
  const [itemForm, setItemForm] = useState({
    service_category_id: '',
    name: '',
    description: '',
    work_description: '',
    default_checklist: [] as Array<{
      label: string
      sort_order: number
      photo_required?: boolean
    }>,
    is_package: false,
    bundle_child_ids: [] as string[],
    unit_type: '',
    base_price: '',
    is_active: true,
    // Phase 11 seasonal fields
    is_seasonal: false,
    season_start_month: '' as string,
    season_end_month: '' as string,
  })
  const emptyItemForm = () => ({
    service_category_id: '',
    name: '',
    description: '',
    work_description: '',
    default_checklist: [] as Array<{
      label: string
      sort_order: number
      photo_required?: boolean
    }>,
    is_package: false,
    bundle_child_ids: [] as string[],
    unit_type: '',
    base_price: '',
    is_active: true,
    is_seasonal: false,
    season_start_month: '',
    season_end_month: '',
  })

  useEffect(() => {
    fetchData()
  }, [currentDivision])

  const fetchData = async () => {
    try {
      setLoading(true)

      // Fetch divisions
      const { data: divisionsData } = await supabase
        .from('operational_divisions')
        .select('*')
        .eq('is_active', true)
        .order('name')

      setDivisions(divisionsData || [])

      // Fetch categories with division info
      let categoriesQuery = supabase
        .from('service_categories')
        .select(
          `
          *,
          operational_division:operational_divisions(id, name, color_code)
        `
        )
        .order('name')

      if (currentDivision) {
        categoriesQuery = categoriesQuery.eq('operational_division_id', currentDivision.id)
      }

      const { data: categoriesData } = await categoriesQuery
      setCategories(categoriesData || [])

      // Fetch service items separately to avoid ambiguous relationship error
      let itemsQuery = supabase.from('service_items').select('*').order('name')

      if (currentDivision) {
        // Filter items by division through their category
        const categoryIds = categoriesData?.map((c) => c.id) || []
        console.log('Current division:', currentDivision.name, 'Category IDs:', categoryIds)
        if (categoryIds.length > 0) {
          itemsQuery = itemsQuery.in('service_category_id', categoryIds)
        } else {
          itemsQuery = itemsQuery.eq('service_category_id', '00000000-0000-0000-0000-000000000000') // Empty result
        }
      }

      const { data: itemsData, error: itemsError } = await itemsQuery

      if (itemsError) {
        console.error('Error fetching service items:', itemsError)
      }

      console.log('Fetched service items:', itemsData)
      console.log('Total items fetched:', itemsData?.length || 0)

      // Manually attach category info to each item
      const itemsWithCategories = (itemsData || []).map((item) => {
        const category = categoriesData?.find((c) => c.id === item.service_category_id)
        return {
          ...item,
          categories: category,
        }
      })

      setServiceItems(itemsWithCategories)

      const itemIds = (itemsData || []).map((i) => i.id)
      if (itemIds.length > 0) {
        const { data: bundleRows } = await supabase
          .from('service_item_bundle_children')
          .select('package_item_id, child_item_id, sort_order')
          .in('package_item_id', itemIds)
          .order('sort_order', { ascending: true })

        const map: Record<string, string[]> = {}
        const childSet = new Set<string>()
        for (const row of bundleRows || []) {
          if (!map[row.package_item_id]) map[row.package_item_id] = []
          map[row.package_item_id].push(row.child_item_id)
          childSet.add(row.child_item_id)
        }
        setPackageChildIds(map)
        setChildItemIdSet(childSet)
      } else {
        setPackageChildIds({})
        setChildItemIdSet(new Set())
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      // Validate required fields
      if (!categoryForm.name.trim()) {
        alert('Please enter a category name.')
        return
      }
      if (!categoryForm.operational_division_id) {
        alert('Please select a division.')
        return
      }

      const categoryData = {
        name: categoryForm.name,
        description: categoryForm.description || null,
        operational_division_id: categoryForm.operational_division_id,
        is_active: categoryForm.is_active,
        updated_at: new Date().toISOString(),
      }

      if (editingCategory) {
        const { error } = await supabase
          .from('service_categories')
          .update(categoryData)
          .eq('id', editingCategory.id)

        if (error) throw error
      } else {
        const { error } = await supabase.from('service_categories').insert([categoryData])

        if (error) throw error
      }

      setShowCategoryForm(false)
      setEditingCategory(null)
      setCategoryForm({ name: '', description: '', operational_division_id: '', is_active: true })
      await fetchData()
    } catch (error) {
      console.error('Error saving category:', error)
      alert('Failed to save category. Please try again.')
    }
  }

  const handleItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!itemForm.service_category_id) {
      alert('Please select a category')
      return
    }
    if (!itemForm.name) {
      alert('Please enter a name')
      return
    }

    try {
      console.log('Submitting service item:', itemForm)

      const itemData: any = {
        service_category_id: itemForm.service_category_id,
        name: itemForm.name,
        description: itemForm.description || null,
        work_description: itemForm.work_description || null,
        default_checklist: normalizeChecklistItems(itemForm.default_checklist),
        is_package: itemForm.is_package || false,
        // Content-only package children often leave these blank → safe defaults
        unit_type: itemForm.unit_type || 'flat_rate',
        base_price: parseFloat(itemForm.base_price) || 0,
        is_active: itemForm.is_active,
        // Phase 11 seasonal fields
        is_seasonal: itemForm.is_seasonal || false,
        season_start_month: itemForm.is_seasonal && itemForm.season_start_month
          ? parseInt(itemForm.season_start_month, 10) || null
          : null,
        season_end_month: itemForm.is_seasonal && itemForm.season_end_month
          ? parseInt(itemForm.season_end_month, 10) || null
          : null,
        updated_at: new Date().toISOString(),
      }

      console.log('Item data to save:', itemData)

      let savedItemId = editingItem?.id || null
      let error
      if (editingItem) {
        console.log('Updating service item:', editingItem.id)
        const result = await supabase
          .from('service_items')
          .update(itemData)
          .eq('id', editingItem.id)
        error = result.error
      } else {
        console.log('Inserting new service item')
        const result = await supabase.from('service_items').insert([itemData]).select('id').single()
        error = result.error
        savedItemId = result.data?.id || null
      }

      if (error) {
        console.error('Supabase error:', error)
        showToast(
          'error',
          `Error: ${error.message}${error.details ? ` — ${error.details}` : ''}`,
          6000
        )
        throw error
      }

      if (savedItemId) {
        await supabase.from('service_item_bundle_children').delete().eq('package_item_id', savedItemId)
        if (itemForm.is_package && itemForm.bundle_child_ids.length > 0) {
          const childRows = itemForm.bundle_child_ids
            .filter((cid) => cid !== savedItemId)
            .map((child_item_id, sort_order) => ({
              package_item_id: savedItemId,
              child_item_id,
              sort_order,
            }))
          if (childRows.length > 0) {
            const { error: bundleError } = await supabase
              .from('service_item_bundle_children')
              .insert(childRows)
            if (bundleError) {
              console.warn('Bundle children save failed:', bundleError)
            }
          }
        }

        // New item created via "Add child" under a package — link in junction table
        if (linkingPackageId && !editingItem && !itemForm.is_package) {
          const existing = packageChildIds[linkingPackageId] || []
          if (!existing.includes(savedItemId)) {
            const { error: linkError } = await supabase.from('service_item_bundle_children').insert({
              package_item_id: linkingPackageId,
              child_item_id: savedItemId,
              sort_order: existing.length,
            })
            if (linkError) {
              console.warn('Package child link failed:', linkError)
              showToast(
                'error',
                `Item saved, but linking to package failed: ${linkError.message}`,
                6000
              )
            }
          }
        }
      }

      setShowItemForm(false)
      setEditingItem(null)
      setLinkingPackageId(null)
      setItemForm(emptyItemForm())
      await fetchData()

      showToast('success', 'Service item saved successfully!')
    } catch (error) {
      console.error('Error saving service item:', error)
      showToast(
        'error',
        `Failed to save service item: ${error instanceof Error ? error.message : 'Unknown error'}`,
        6000
      )
    }
  }

  const handleEditCategory = (category: ServiceCategory) => {
    setEditingCategory(category)
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      operational_division_id: category.operational_division_id || '',
      is_active: category.is_active,
    })
    setShowCategoryForm(true)
  }

  const handleEditItem = async (item: ServiceItem) => {
    setLinkingPackageId(null)
    setEditingItem(item)
    let bundleChildIds: string[] = []
    if (item.is_package) {
      const { data: children } = await supabase
        .from('service_item_bundle_children')
        .select('child_item_id')
        .eq('package_item_id', item.id)
        .order('sort_order', { ascending: true })
      bundleChildIds = (children || []).map((c) => c.child_item_id)
    }
    setItemForm({
      service_category_id: item.service_category_id,
      name: item.name,
      description: item.description || '',
      work_description: item.work_description || '',
      default_checklist: normalizeChecklistItems(item.default_checklist),
      is_package: !!item.is_package,
      bundle_child_ids: bundleChildIds,
      unit_type: item.unit_type,
      base_price: item.base_price.toString(),
      is_active: item.is_active,
      is_seasonal: (item as any).is_seasonal || false,
      season_start_month: (item as any).season_start_month
        ? String((item as any).season_start_month)
        : '',
      season_end_month: (item as any).season_end_month
        ? String((item as any).season_end_month)
        : '',
    })
    setShowItemForm(true)
  }

  const handleDeleteCategory = async (category: ServiceCategory) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${category.name}"? This will also delete all associated service items.`
      )
    ) {
      return
    }

    try {
      const { error } = await supabase.from('service_categories').delete().eq('id', category.id)

      if (error) throw error

      await fetchData()
    } catch (error) {
      console.error('Error deleting category:', error)
      alert('Failed to delete category. Please try again.')
    }
  }

  const handleDeleteItem = async (item: ServiceItem) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      return
    }

    try {
      const { error } = await supabase.from('service_items').delete().eq('id', item.id)

      if (error) throw error

      await fetchData()
    } catch (error) {
      console.error('Error deleting service item:', error)
      alert('Failed to delete service item. Please try again.')
    }
  }

  const getCategoryIcon = (categoryName: string) => {
    if (categoryName.includes('Maintenance')) return HammerIcon
    if (categoryName.includes('Office')) return BriefcaseIcon
    if (categoryName.includes('Apartment') || categoryName.includes('Post-Construction'))
      return BuildingIcon
    return WrenchIcon
  }

  const getUnitTypeLabel = (unitType: string) => {
    const labels: { [key: string]: string } = {
      monthly: 'Monthly',
      per_cleaning: 'Per Cleaning',
      per_unit: 'Per Unit',
      per_project: 'Per Project',
      hourly: 'Hourly',
    }
    return labels[unitType] || unitType
  }

  if (loading) {
    return (
      <MobilePage>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </MobilePage>
    )
  }

  const openNewItemForm = (categoryId?: string) => {
    setLinkingPackageId(null)
    setEditingItem(null)
    setItemForm({
      ...emptyItemForm(),
      service_category_id: categoryId || '',
    })
    setShowItemForm(true)
  }

  const openNewChildItemForm = (packageItem: ServiceItem) => {
    setLinkingPackageId(packageItem.id)
    setEditingItem(null)
    setItemForm({
      ...emptyItemForm(),
      service_category_id: packageItem.service_category_id,
    })
    setExpandedPackages((prev) => new Set(prev).add(packageItem.id))
    setShowItemForm(true)
  }

  const togglePackageExpanded = (packageId: string) => {
    setExpandedPackages((prev) => {
      const next = new Set(prev)
      if (next.has(packageId)) next.delete(packageId)
      else next.add(packageId)
      return next
    })
  }

  return (
    <MobilePage>
      {notification && (
        <div
          className={`p-3 sm:p-4 rounded-xl border ${
            notification.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium">{notification.message}</p>
            <button
              type="button"
              onClick={() => setNotification(null)}
              className={mobile.ghostBtn}
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <MobilePageHeader
        title="Service Catalog"
        subtitle={
          currentDivision ? (
            <span className="inline-flex items-center gap-2">
              Manage categories and items
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: currentDivision.color_code }}
              >
                {currentDivision.name}
              </span>
            </span>
          ) : (
            'Manage categories and items'
          )
        }
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setEditingCategory(null)
                setCategoryForm({
                  name: '',
                  description: '',
                  operational_division_id: '',
                  is_active: true,
                })
                setShowCategoryForm(true)
              }}
              className={`${mobile.secondaryBtn} px-3`}
              title="Add Category"
            >
              <PlusIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Category</span>
            </button>
            <button
              type="button"
              onClick={() => openNewItemForm()}
              className={`${mobile.primaryBtn} px-3`}
              title="Add Service Item"
            >
              <PlusIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Item</span>
            </button>
          </div>
        }
      />

      {categories.length === 0 ? (
        <MobileEmptyState
          title="No service categories yet"
          description="Add a category to start grouping items."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {categories.map((category) => {
            const Icon = getCategoryIcon(category.name)
            const categoryItems = serviceItems.filter(
              (item) => item.service_category_id === category.id
            )
            const topLevelItems = categoryItems.filter((item) => !childItemIdSet.has(item.id))
            return (
              <MobileSection
                key={category.id}
                title={category.name}
                description={`${topLevelItems.length} items`}
                actions={
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => handleEditCategory(category)}
                      className={mobile.ghostBtn}
                      title="Edit category"
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteCategory(category)}
                      className={`${mobile.ghostBtn} text-red-500`}
                      title="Delete category"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                }
                flush
              >
                <div className="px-4 pb-2 flex flex-wrap items-center gap-1.5">
                  <div className="p-1.5 bg-blue-50 rounded-lg">
                    <Icon className="w-4 h-4 text-blue-600" />
                  </div>
                  {category.operational_division && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white"
                      style={{
                        backgroundColor: category.operational_division.color_code,
                      }}
                    >
                      {category.operational_division.name}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      category.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {category.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {topLevelItems.length === 0 ? (
                  <p className="text-xs text-gray-500 px-4 py-3">No items yet.</p>
                ) : (
                  <ul className="divide-y divide-gray-100 flex-1">
                    {topLevelItems.map((item) => {
                      const childIds = packageChildIds[item.id] || []
                      const isExpanded = expandedPackages.has(item.id)
                      return (
                        <li key={item.id} className="px-3 py-2.5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1 flex items-start gap-0.5">
                              {item.is_package ? (
                                <button
                                  type="button"
                                  onClick={() => togglePackageExpanded(item.id)}
                                  className="mt-0.5 min-h-[36px] min-w-[36px] inline-flex items-center justify-center text-gray-400 hover:text-gray-600 flex-shrink-0"
                                  title={isExpanded ? 'Collapse children' : 'Expand children'}
                                >
                                  {isExpanded ? (
                                    <ChevronDownIcon className="w-4 h-4" />
                                  ) : (
                                    <ChevronRightIcon className="w-4 h-4" />
                                  )}
                                </button>
                              ) : (
                                <span className="w-9 flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <button
                                    type="button"
                                    onClick={() => setViewingItem(item)}
                                    className="text-sm font-medium text-gray-900 truncate text-left hover:text-blue-700 min-h-[36px]"
                                    title="View service item"
                                  >
                                    {item.name}
                                  </button>
                                  {item.is_package && (
                                    <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-800">
                                      Pkg
                                      {childIds.length > 0 ? ` · ${childIds.length}` : ''}
                                    </span>
                                  )}
                                  {!item.is_active && (
                                    <span className="flex-shrink-0 text-[10px] text-red-600">
                                      Off
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500">
                                  ${Number(item.base_price || 0).toFixed(2)} ·{' '}
                                  {getUnitTypeLabel(item.unit_type)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => setViewingItem(item)}
                                className={mobile.ghostBtn}
                                title="View"
                              >
                                <EyeIcon className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEditItem(item)}
                                className={mobile.ghostBtn}
                                title="Edit"
                              >
                                <EditIcon className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteItem(item)}
                                className={`${mobile.ghostBtn} text-red-500`}
                                title="Delete"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {item.is_package && isExpanded && (
                            <ul className="mt-1 ml-5 pl-2 border-l border-indigo-100 space-y-0.5">
                              {childIds.map((childId) => {
                                const child = serviceItems.find((s) => s.id === childId)
                                if (!child) return null
                                return (
                                  <li
                                    key={child.id}
                                    className="py-1 flex items-center justify-between gap-2"
                                  >
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        <button
                                          type="button"
                                          onClick={() => setViewingItem(child)}
                                          className="text-xs font-medium text-gray-800 truncate text-left hover:text-blue-700"
                                          title="View service item"
                                        >
                                          {child.name}
                                        </button>
                                        {!child.is_active && (
                                          <span className="flex-shrink-0 text-[10px] text-red-600">
                                            Off
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-[10px] text-gray-500">
                                        ${Number(child.base_price || 0).toFixed(2)} ·{' '}
                                        {getUnitTypeLabel(child.unit_type)}
                                      </p>
                                    </div>
                                    <div className="flex space-x-0.5 flex-shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => setViewingItem(child)}
                                        className="p-1 text-gray-400 hover:text-blue-600"
                                        title="View"
                                      >
                                        <EyeIcon className="w-3 h-3" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleEditItem(child)}
                                        className="p-1 text-gray-400 hover:text-gray-600"
                                        title="Edit"
                                      >
                                        <EditIcon className="w-3 h-3" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteItem(child)}
                                        className="p-1 text-gray-400 hover:text-red-600"
                                        title="Delete"
                                      >
                                        <TrashIcon className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </li>
                                )
                              })}
                              {childIds.length === 0 && (
                                <li className="py-1 text-[10px] text-gray-400">No children yet</li>
                              )}
                              <li className="pt-0.5">
                                <button
                                  type="button"
                                  onClick={() => openNewChildItemForm(item)}
                                  className="inline-flex items-center text-[11px] font-medium text-indigo-700 hover:text-indigo-900"
                                >
                                  <PlusIcon className="w-3 h-3 mr-0.5" />
                                  Add child
                                </button>
                              </li>
                            </ul>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                )}

                <div className="px-3 pb-3 pt-1">
                  <button
                    type="button"
                    onClick={() => openNewItemForm(category.id)}
                    className="inline-flex items-center justify-center w-full min-h-[44px] px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-xl hover:bg-blue-100 active:bg-blue-200"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    Add item
                  </button>
                </div>
              </MobileSection>
            )
          })}
        </div>
      )}

        {/* Category Form Modal */}
        {showCategoryForm && (
          <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50 flex items-start justify-center px-3 py-6">
            <div className="relative w-full max-w-md p-5 border shadow-lg rounded-xl bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingCategory ? 'Edit Category' : 'Add Category'}
                  </h3>
                  <button
                    onClick={() => setShowCategoryForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XIcon className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleCategorySubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name *</label>
                    <input
                      type="text"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={categoryForm.description}
                      onChange={(e) =>
                        setCategoryForm({ ...categoryForm, description: e.target.value })
                      }
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Division *</label>
                    <select
                      value={categoryForm.operational_division_id}
                      onChange={(e) =>
                        setCategoryForm({
                          ...categoryForm,
                          operational_division_id: e.target.value,
                        })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Division</option>
                      {divisions.map((division) => (
                        <option key={division.id} value={division.id}>
                          {division.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="category-active"
                      checked={categoryForm.is_active}
                      onChange={(e) =>
                        setCategoryForm({ ...categoryForm, is_active: e.target.checked })
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="category-active" className="ml-2 block text-sm text-gray-900">
                      Active
                    </label>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowCategoryForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      {editingCategory ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Service Item Form Modal */}
        {showItemForm && (
          <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50 flex items-start justify-center px-3 py-6">
            <div className="relative w-full max-w-md p-5 border shadow-lg rounded-xl bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingItem
                      ? 'Edit Service Item'
                      : linkingPackageId
                        ? 'Add package child'
                        : 'Add Service Item'}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowItemForm(false)
                      setLinkingPackageId(null)
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XIcon className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleItemSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Category *</label>
                    <select
                      value={itemForm.service_category_id}
                      onChange={(e) =>
                        setItemForm({ ...itemForm, service_category_id: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name *</label>
                    <input
                      type="text"
                      value={itemForm.name}
                      onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={itemForm.description}
                      onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Work to be done
                    </label>
                    <textarea
                      value={itemForm.work_description}
                      onChange={(e) =>
                        setItemForm({ ...itemForm, work_description: e.target.value })
                      }
                      rows={3}
                      placeholder="Seeded into jobs when this item is on a visit"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Default checklist
                    </label>
                    <p className="text-xs text-gray-500">
                      Each task is copied onto jobs (prefixed with this item&apos;s name).
                    </p>
                    {itemForm.default_checklist.map((row, idx) => (
                      <div key={idx} className="flex flex-wrap items-center gap-2">
                        <input
                          type="text"
                          value={row.label}
                          onChange={(e) => {
                            const next = [...itemForm.default_checklist]
                            next[idx] = { ...next[idx], label: e.target.value }
                            // Do not normalize/trim while typing — trim drops trailing spaces
                            setItemForm({ ...itemForm, default_checklist: next })
                          }}
                          className="flex-1 min-w-[10rem] border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                          placeholder="Checklist task…"
                        />
                        <label className="inline-flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={!!row.photo_required}
                            onChange={(e) => {
                              const next = [...itemForm.default_checklist]
                              next[idx] = {
                                ...next[idx],
                                photo_required: e.target.checked,
                              }
                              setItemForm({ ...itemForm, default_checklist: next })
                            }}
                            className="rounded border-gray-300"
                          />
                          Photo required
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const next = itemForm.default_checklist.filter((_, i) => i !== idx)
                            setItemForm({
                              ...itemForm,
                              default_checklist: normalizeChecklistItems(next),
                            })
                          }}
                          className="text-red-500 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        setItemForm({
                          ...itemForm,
                          default_checklist: [
                            ...itemForm.default_checklist,
                            {
                              label: '',
                              sort_order: itemForm.default_checklist.length,
                              photo_required: false,
                            },
                          ],
                        })
                      }
                      className="text-sm text-blue-600 hover:underline"
                    >
                      + Add task
                    </button>
                  </div>
                  {!linkingPackageId && (
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="item-package"
                        checked={itemForm.is_package}
                        onChange={(e) =>
                          setItemForm({ ...itemForm, is_package: e.target.checked })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="item-package" className="ml-2 block text-sm text-gray-900">
                        Package (one line item, child items for ops)
                      </label>
                    </div>
                    {itemForm.is_package && (
                      <div className="max-h-40 overflow-y-auto space-y-1 border border-gray-200 rounded-md bg-white p-2">
                        {serviceItems
                          .filter((si) => si.id !== editingItem?.id && !si.is_package)
                          .map((si) => {
                            const checked = itemForm.bundle_child_ids.includes(si.id)
                            return (
                              <label
                                key={si.id}
                                className="flex items-center gap-2 text-sm text-gray-800"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    const next = checked
                                      ? itemForm.bundle_child_ids.filter((id) => id !== si.id)
                                      : [...itemForm.bundle_child_ids, si.id]
                                    setItemForm({ ...itemForm, bundle_child_ids: next })
                                  }}
                                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                />
                                {si.name}
                              </label>
                            )
                          })}
                      </div>
                    )}
                  </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Unit Type</label>
                    <select
                      value={itemForm.unit_type}
                      onChange={(e) => setItemForm({ ...itemForm, unit_type: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Flat rate (default)</option>
                      <option value="flat_rate">Flat Rate</option>
                      <option value="monthly">Monthly</option>
                      <option value="per_cleaning">Per Cleaning</option>
                      <option value="per_unit">Per Unit</option>
                      <option value="per_project">Per Project</option>
                      <option value="hourly">Hourly</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      For package children (ops only), leave blank — billing comes from the parent
                      package. Defaults to flat rate.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Base Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={itemForm.base_price}
                      onChange={(e) => setItemForm({ ...itemForm, base_price: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Optional for content-only items inside a package (defaults to $0). Set the sold
                      price on the package item itself.
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="item-active"
                      checked={itemForm.is_active}
                      onChange={(e) => setItemForm({ ...itemForm, is_active: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="item-active" className="ml-2 block text-sm text-gray-900">
                      Active
                    </label>
                  </div>

                  {/* Phase 11: Seasonal service flag */}
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="item-seasonal"
                        checked={itemForm.is_seasonal}
                        onChange={(e) =>
                          setItemForm({ ...itemForm, is_seasonal: e.target.checked })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="item-seasonal" className="ml-2 block text-sm text-gray-900">
                        Seasonal service
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">
                      Seasonal services (e.g., snow removal, lawn care) expose per-visit-group date inputs so the contract can limit them to the right months.
                    </p>
                    {itemForm.is_seasonal && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Season start month
                          </label>
                          <select
                            value={itemForm.season_start_month}
                            onChange={(e) =>
                              setItemForm({ ...itemForm, season_start_month: e.target.value })
                            }
                            className="block w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                          >
                            <option value="">--</option>
                            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map(
                              (label, idx) => (
                                <option key={idx + 1} value={String(idx + 1)}>
                                  {label}
                                </option>
                              )
                            )}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Season end month
                          </label>
                          <select
                            value={itemForm.season_end_month}
                            onChange={(e) =>
                              setItemForm({ ...itemForm, season_end_month: e.target.value })
                            }
                            className="block w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                          >
                            <option value="">--</option>
                            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map(
                              (label, idx) => (
                                <option key={idx + 1} value={String(idx + 1)}>
                                  {label}
                                </option>
                              )
                            )}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowItemForm(false)
                        setLinkingPackageId(null)
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      {editingItem ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        {/* Service Item View Modal */}
        {viewingItem && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-16 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white mb-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">View Service Item</h3>
                <button
                  type="button"
                  onClick={() => setViewingItem(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</p>
                  <p className="text-gray-900 font-medium mt-0.5">
                    {viewingItem.name}
                    {viewingItem.is_package && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-800">
                        Package
                      </span>
                    )}
                    <span
                      className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        viewingItem.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {viewingItem.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Category
                  </p>
                  <p className="text-gray-900 mt-0.5">
                    {categories.find((c) => c.id === viewingItem.service_category_id)?.name || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Description
                  </p>
                  <p className="text-gray-900 mt-0.5 whitespace-pre-wrap">
                    {viewingItem.description?.trim() || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Work to be done
                  </p>
                  <p className="text-gray-900 mt-0.5 whitespace-pre-wrap">
                    {viewingItem.work_description?.trim() || '—'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Unit type
                    </p>
                    <p className="text-gray-900 mt-0.5">
                      {getUnitTypeLabel(viewingItem.unit_type) || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Base price
                    </p>
                    <p className="text-gray-900 mt-0.5">
                      ${Number(viewingItem.base_price || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
                {viewingItem.is_seasonal && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Season
                    </p>
                    <p className="text-gray-900 mt-0.5">
                      {['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
                        viewingItem.season_start_month || 0
                      ] || '?'}{' '}
                      –{' '}
                      {['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
                        viewingItem.season_end_month || 0
                      ] || '?'}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Default checklist
                  </p>
                  {normalizeChecklistItems(viewingItem.default_checklist).length === 0 ? (
                    <p className="text-gray-500 mt-0.5">None</p>
                  ) : (
                    <ul className="mt-1 space-y-1">
                      {normalizeChecklistItems(viewingItem.default_checklist).map((row, idx) => (
                        <li
                          key={idx}
                          className="flex items-center justify-between gap-2 text-gray-900 border border-gray-100 rounded px-2 py-1"
                        >
                          <span>{row.label}</span>
                          {row.photo_required && (
                            <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                              Photo req.
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {viewingItem.is_package && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Package children
                    </p>
                    {(packageChildIds[viewingItem.id] || []).length === 0 ? (
                      <p className="text-gray-500 mt-0.5">None</p>
                    ) : (
                      <ul className="mt-1 list-disc list-inside text-gray-900">
                        {(packageChildIds[viewingItem.id] || []).map((cid) => {
                          const child = serviceItems.find((s) => s.id === cid)
                          return <li key={cid}>{child?.name || cid}</li>
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setViewingItem(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const item = viewingItem
                    setViewingItem(null)
                    handleEditItem(item)
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <EditIcon className="w-4 h-4 mr-1.5" />
                  Edit
                </button>
              </div>
            </div>
          </div>
        )}
    </MobilePage>
  )
}
